import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import { Modal, useToast } from '../../componentes/ui';
import { api, patch, post } from '../../lib/api';
import { useEtiquetas, type Etiqueta } from '../../lib/consultas';

const CORES = [
  '#001b82',
  '#1daa61',
  '#c2362b',
  '#a86300',
  '#7a3fd1',
  '#0b6e99',
  '#b8326e',
  '#5d6678',
];

export function Etiquetas() {
  const lista = useEtiquetas().data ?? [];
  const [editando, setEditando] = useState<Etiqueta | 'nova' | null>(null);
  const [apagando, setApagando] = useState<Etiqueta | null>(null);

  return (
    <div className="config-cartao largo">
      <header className="com-acao">
        <div>
          <h2>Etiquetas</h2>
          <p>Marque as conversas por assunto ou tipo de cliente.</p>
        </div>
        <button className="btn primario" onClick={() => setEditando('nova')}>
          <Plus /> Nova etiqueta
        </button>
      </header>
      {lista.length === 0 ? (
        <p className="nota-sec">Nenhuma etiqueta ainda.</p>
      ) : (
        <ul className="lista-config">
          {lista.map((t) => (
            <li key={t.id}>
              <span className="etq-nome" style={{ '--etq': t.cor } as CSSProperties}>
                <span className="etq-ponto" />
                {t.nome}
              </span>
              <span className="lista-acoes">
                <button
                  className="icone-btn sem-borda"
                  aria-label={`Editar ${t.nome}`}
                  onClick={() => setEditando(t)}
                >
                  <Pencil />
                </button>
                <button
                  className="icone-btn sem-borda perigo"
                  aria-label={`Apagar ${t.nome}`}
                  onClick={() => setApagando(t)}
                >
                  <Trash2 />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {editando && (
        <ModalEtiqueta
          atual={editando === 'nova' ? null : editando}
          fechar={() => setEditando(null)}
        />
      )}
      {apagando && <ModalApagarEtiqueta t={apagando} fechar={() => setApagando(null)} />}
    </div>
  );
}

function ModalEtiqueta({ atual, fechar }: { atual: Etiqueta | null; fechar: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nome, setNome] = useState(atual?.nome ?? '');
  const [cor, setCor] = useState(atual?.cor ?? CORES[0]!);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    try {
      if (atual) await patch(`/api/etiquetas/${atual.id}`, { nome, cor });
      else await post('/api/etiquetas', { nome, cor });
      qc.invalidateQueries({ queryKey: ['etiquetas'] });
      qc.invalidateQueries({ queryKey: ['conversas'] });
      toast(atual ? 'Etiqueta atualizada' : 'Etiqueta criada');
      fechar();
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  return (
    <Modal titulo={atual ? `Editar ${atual.nome}` : 'Nova etiqueta'} aoFechar={fechar}>
      <label className="campo">
        <span>Nome</span>
        <input
          id="etq-nome"
          className="input"
          value={nome}
          maxLength={40}
          onChange={(e) => setNome(e.target.value)}
        />
      </label>
      <div className="campo">
        <span>Cor</span>
        <div className="etq-cores" role="radiogroup" aria-label="Cor">
          {CORES.map((k) => (
            <button
              key={k}
              role="radio"
              aria-checked={cor === k}
              aria-label={k}
              style={{ background: k }}
              onClick={() => setCor(k)}
            />
          ))}
        </div>
      </div>
      {erro && <div className="erro-form">{erro}</div>}
      <div className="acoes">
        <button className="btn" onClick={fechar}>
          Cancelar
        </button>
        <button className="btn primario" disabled={!nome.trim()} onClick={() => void salvar()}>
          Salvar
        </button>
      </div>
    </Modal>
  );
}

function ModalApagarEtiqueta({ t, fechar }: { t: Etiqueta; fechar: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  async function apagar() {
    try {
      await api(`/api/etiquetas/${t.id}`, { metodo: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['etiquetas'] });
      qc.invalidateQueries({ queryKey: ['conversas'] });
      toast('Etiqueta apagada');
      fechar();
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }
  return (
    <Modal titulo="Apagar etiqueta" aoFechar={fechar}>
      <p style={{ margin: 0 }}>
        Apagar <b>{t.nome}</b>? Ela sai de todas as conversas que estavam marcadas.
      </p>
      <div className="acoes">
        <button className="btn" onClick={fechar}>
          Cancelar
        </button>
        <button className="btn perigo-cheio" onClick={() => void apagar()}>
          Apagar
        </button>
      </div>
    </Modal>
  );
}
